interface DreamAnalysisData {
  dreamContent: string;
  emotions: string;
  realLifeInfoPonctuel?: string;
  realLifeInfoPermanent?: string;
  detectedGenderLabel?: string;
}

const buildGenderSentence = (language: string, detectedGenderLabel?: string) => {
  if (!detectedGenderLabel) return '';

  switch (language) {
    case 'fr':
      return `\nInformation personnelle : le rêveur se définit comme ${detectedGenderLabel}.`;
    case 'es':
      return `\nInformación personal: la persona que sueña se define como ${detectedGenderLabel}.`;
    default:
      return `\nPersonal info: the dreamer identifies as a ${detectedGenderLabel}.`;
  }
};

const getLanguageInstruction = (language: string): string => {
  switch (language) {
    case 'fr':
      return 'IMPORTANT : Répondez EXCLUSIVEMENT EN FRANÇAIS. Toute l\'analyse doit être en français.';
    case 'en':
      return 'IMPORTANT: Respond EXCLUSIVELY IN ENGLISH. The entire analysis must be in English.';
    case 'es':
      return 'IMPORTANTE: Responda EXCLUSIVAMENTE EN ESPAÑOL. Todo el análisis debe estar en español.';
    default:
      return 'IMPORTANT: Respond EXCLUSIVELY IN ENGLISH. The entire analysis must be in English.';
  }
};

// Fonction pour déterminer les paramètres d'analyse en fonction de la longueur du rêve
const getDreamAnalysisParameters = (dreamText: string) => {
  // Calculer le nombre de mots dans le rêve
  const dreamWords = dreamText.split(/\s+/).filter(word => word.length > 0).length;
  
  // Définir les paramètres en fonction de la longueur
  if (dreamWords < 20) {
    // Rêve très court
    return {
      minWords: 100,
      maxWords: 150,
      keywordsCount: 1,
      structureDescription: "- Présentez brièvement le rêve et ses symboles principaux\n- Proposez une interprétation concise"
    };
  } else if (dreamWords < 50) {
    // Rêve court
    return {
      minWords: 200,
      maxWords: 250,
      keywordsCount: 2,
      structureDescription: "- Présentez les éléments marquants du rêve\n- Analysez les symboles principaux et leur signification\n- Concluez avec une interprétation"
    };
  } else if (dreamWords < 100) {
    // Rêve moyen
    return {
      minWords: 300,
      maxWords: 350,
      keywordsCount: 3,
      structureDescription: "- Présentez les éléments marquants du rêve et le contexte\n- Analysez en détail les symboles et leur signification\n- Concluez avec un message inspirant"
    };
  } else {
    // Rêve long
    return {
      minWords: 350,
      maxWords: 450,
      keywordsCount: 5,
      structureDescription: "- Présentez les éléments marquants du rêve et le contexte général\n- Analysez en profondeur les symboles et leur signification\n- Explorez les connexions entre les différents éléments du rêve\n- Justifiez le score de sérénité attribué\n- Concluez avec un message inspirant pour guider le rêveur"
    };
  }
};

const getResponseFormat = (language: string, dreamText: string) => {
  const params = getDreamAnalysisParameters(dreamText);
  
  return `
${getLanguageInstruction(language)}

Format de réponse attendu (IMPORTANT : respectez EXACTEMENT cette structure) :
Votre analyse doit être structurée en parties distinctes, séparées par des sauts de ligne, pour un total de ${params.minWords} à ${params.maxWords} mots :

${params.structureDescription}

IMPORTANT :
- N'intégrez les informations contextuelles (ponctuelles ou permanentes) QUE si elles sont essentielles à la compréhension du rêve. Si elles ne sont pas directement pertinentes pour l'analyse, ignorez-les complètement.
- Mentionne explicitement, lorsque l'information est fournie, le genre du rêveur tel qu'il se définit (ex : "rêveuse", "rêveur", "personne non binaire"). N'invente rien si le genre n'est pas précisé.
- Décris dans le résumé les éléments visuels concrets : palette de couleurs dominantes, textures ou matériaux perceptibles, formes géométriques ou organiques marquantes.
- Fournissez également un résumé immersif ET FACTUEL de 35 à 45 mots EXACTEMENT, décrivant UNIQUEMENT le rêve tel qu'il est raconté par l'utilisateur (personnages, actions, lieux, sensations). Aucune interprétation, aucun conseil, aucune mention du score de sérénité. aucun nom ou prénom de personnage.

Répondez en format JSON :
{
  "analysis": "VOTRE ANALYSE EN PLUSIEURES PARTIES AVEC DES SAUTS DE LIGNE ENTRE CHAQUE PARTIE",
  "keywords": [${Array(params.keywordsCount).fill('"mot"').join(', ')}],
  "serenityScore": number // 0-100,
  "summary": "Résumé narratif du rêve (35 à 45 mots) reprenant UNIQUEMENT les éléments du récit initial, sans interprétation, en mentionnant le genre du rêveur (si précisé) et les couleurs/textures/formes marquantes"
}
  
IMPORTANT : Répondez OBLIGATOIREMENT avec la langue demandée.`;
};

export const buildSingleApproachPrompt = (
  approachPrompt: string,
  data: DreamAnalysisData,
  language: string
) => {
  // Récupérer les paramètres d'analyse basés sur la longueur du rêve
  const params = getDreamAnalysisParameters(data.dreamContent);
  
  // Simplifier le prompt de l'analyste pour les rêves courts
  let adaptedPrompt = approachPrompt;
  
  // Pour les rêves très courts ou courts, simplifier la structure demandée
  if (data.dreamContent.split(/\s+/).filter(word => word.length > 0).length < 50) {
    // Créer une instruction explicite sur le nombre de mots-clés
    const keywordInstruction = `\n\nIMPORTANT : Pour ce rêve court, identifie EXACTEMENT ${params.keywordsCount} mot${params.keywordsCount > 1 ? 's' : ''}-clé${params.keywordsCount > 1 ? 's' : ''} essentiel${params.keywordsCount > 1 ? 's' : ''}, ni plus ni moins.`;
    
    // Ajouter cette instruction juste avant les consignes stylistiques
    if (adaptedPrompt.includes("CONSIGNES STYLISTIQUES")) {
      adaptedPrompt = adaptedPrompt.replace("CONSIGNES STYLISTIQUES", keywordInstruction + "\n\nCONSIGNES STYLISTIQUES");
    } else {
      // Si pas de consignes stylistiques, ajouter à la fin du prompt
      adaptedPrompt += keywordInstruction;
    }
  }
  
  const genderSentence = buildGenderSentence(language, data.detectedGenderLabel);

  return `
${adaptedPrompt.replace(
  '[Insérer ici la description du rêve donnée par l\'utilisateur]',
  data.dreamContent
).replace(
  '[Insérer ici les émotions facultatives exprimées par l\'utilisateur]',
  data.emotions || "Non spécifiées"
).replace(
  '[Insérer ici les informations ponctuelles facultatives]',
  data.realLifeInfoPonctuel || "Non spécifiées"
).replace(
  '[Insérer ici les informations permanentes facultatives]',
  data.realLifeInfoPermanent || "Non spécifiées"
)}

${genderSentence}
${getResponseFormat(language, data.dreamContent)}`;
};

export const buildDualApproachPrompt = (
  approach1: { name: string; prompt: string },
  approach2: { name: string; prompt: string },
  data: DreamAnalysisData,
  language: string
) => {
  // Récupérer les paramètres d'analyse basés sur la longueur du rêve
  const params = getDreamAnalysisParameters(data.dreamContent);
  
  // Simplifier les prompts des analystes pour les rêves courts
  let adaptedPrompt1 = approach1.prompt;
  let adaptedPrompt2 = approach2.prompt;
  
  // Pour les rêves très courts ou courts, simplifier la structure demandée
  if (data.dreamContent.split(/\s+/).filter(word => word.length > 0).length < 50) {
    // Créer une instruction explicite sur le nombre de mots-clés
    const keywordInstruction = `\n\nIMPORTANT : Pour ce rêve court, identifie EXACTEMENT ${params.keywordsCount} mot${params.keywordsCount > 1 ? 's' : ''}-clé${params.keywordsCount > 1 ? 's' : ''} essentiel${params.keywordsCount > 1 ? 's' : ''}, ni plus ni moins.`;
    
    // Adapter le premier prompt
    if (adaptedPrompt1.includes("CONSIGNES STYLISTIQUES")) {
      adaptedPrompt1 = adaptedPrompt1.replace("CONSIGNES STYLISTIQUES", keywordInstruction + "\n\nCONSIGNES STYLISTIQUES");
    } else {
      // Si pas de consignes stylistiques, ajouter à la fin du prompt
      adaptedPrompt1 += keywordInstruction;
    }
    
    // Adapter le second prompt
    if (adaptedPrompt2.includes("CONSIGNES STYLISTIQUES")) {
      adaptedPrompt2 = adaptedPrompt2.replace("CONSIGNES STYLISTIQUES", keywordInstruction + "\n\nCONSIGNES STYLISTIQUES");
    } else {
      // Si pas de consignes stylistiques, ajouter à la fin du prompt
      adaptedPrompt2 += keywordInstruction;
    }
  }
  
  const genderSentence = buildGenderSentence(language, data.detectedGenderLabel);

  return `
Analysez ce rêve en combinant deux approches différentes :

APPROCHE 1 : ${approach1.name}
${adaptedPrompt1.replace(
  '[Insérer ici la description du rêve donnée par l\'utilisateur]',
  data.dreamContent
).replace(
  '[Insérer ici les émotions facultatives exprimées par l\'utilisateur]',
  data.emotions || "Non spécifiées"
).replace(
  '[Insérer ici les informations ponctuelles facultatives]',
  data.realLifeInfoPonctuel || "Non spécifiées"
).replace(
  '[Insérer ici les informations permanentes facultatives]',
  data.realLifeInfoPermanent || "Non spécifiées"
)}

APPROCHE 2 : ${approach2.name}
${adaptedPrompt2.replace(
  '[Insérer ici la description du rêve donnée par l\'utilisateur]',
  data.dreamContent
).replace(
  '[Insérer ici les émotions facultatives exprimées par l\'utilisateur]',
  data.emotions || "Non spécifiées"
).replace(
  '[Insérer ici les informations ponctuelles facultatives]',
  data.realLifeInfoPonctuel || "Non spécifiées"
).replace(
  '[Insérer ici les informations permanentes facultatives]',
  data.realLifeInfoPermanent || "Non spécifiées"
)}

${genderSentence}

Pour l'analyse combinée :
- Identifiez les points communs et les différences entre les deux approches
- Créez une synthèse qui intègre les perspectives des deux approches
- Le développement doit faire entre ${params.minWords} et ${params.maxWords} mots

${getResponseFormat(language, data.dreamContent)}`;
};
